import { AxiosResponse } from "axios";
import { ResponsePayloadV2 } from "@/domain/entities/ResponsePayload";
import { PaginationV2 } from "@/domain/entities/Pagination";
import {
  RevenueBreakdownData,
  ChartPoint,
  BreakdownItem,
  CustomerLeaderboardResponse,
  CustomerLeaderboard,
  CustomerLeaderboardPerformance,
  CustomerLeaderboardPerformancePeriode
} from "@/domain/entities/RevenueDashboard";
import {
  PosRevenueOverview,
  RevenueOverviewData,
  Breakdown,
  RevenueDashboardBreakdownChartDataItem,
  RevenueDashboardBreakdownDataPerSection
} from "@/domain/entities/RevenueDashboard";

export class RevenueDashboardMapper {
  convertRevenueOverviewFromApi(result: AxiosResponse<any>) {
    const { data: { data } } = result;

    return new PosRevenueOverview({
      overview: new RevenueOverviewData({
        totalRevenue: data.overview.total_revenue,
        totalStt: data.overview.total_stt,
        totalProfit: data.overview.total_profit,
        earnedProfit: data.overview.earned_profit,
        pendingProfit: data.overview.pending_profit
      }),
      comparison: new RevenueOverviewData({
        totalRevenue: data.comparison.total_revenue,
        totalStt: data.comparison.total_stt
      }),
      totalRevenueDifferencePercentage: data.total_revenue_difference_percentage,
      totalProfitDifferencePercentage: data.total_profit_difference_percentage,
      totalSttDifferencePercentage: data.total_stt_difference_percentage
    });
  }

public convertCustomerLeaderboardFromApi(
    result: AxiosResponse<any>
  ): CustomerLeaderboardResponse {
    const { data } = result.data;

    return new CustomerLeaderboardResponse({
      comparisonMonths: data?.comparison_months ?? [],
      lastSyncAt: data?.last_sync_at ?? "",
      periodLabel: data?.period_label ?? "",
      data: data?.data
        ? data?.data.map(
            (item: any) =>
              new CustomerLeaderboard({
                rank: Number(item?.rank ?? 0),
                rankChange: item.rank_change,
                phoneNumber: item.phone,
                name: item.name,
                lastBooking: item.last_booking_at,
                tag: item.tags,
                performance: new CustomerLeaderboardPerformance({
                  currentPeriode: new CustomerLeaderboardPerformancePeriode({
                    amount: Number(item?.performance?.current_period?.revenue ?? 0),
                    stt: Number(item?.performance?.current_period?.stt ?? 0),
                    weightKg: Number(item?.performance?.current_period?.weight_kg ?? 0)
                  }),
                  previousPeriode: item?.performance?.previous_months?.map(
                    (period: any) =>
                      new CustomerLeaderboardPerformancePeriode({
                        month: period?.label ?? "",
                        growthPercentage: Number(
                          period?.growth_percentage ?? 0
                        ),
                        amount: Number(period?.revenue ?? 0),
                        stt: Number(period?.stt ?? 0),
                        weightKg: Number(period?.weight_kg ?? 0)
                      })
                  )
                })
              })
          )
        : []
    });
  }
  
  public convertProductTypesFromApi(result: AxiosResponse<any>): ResponsePayloadV2 {
    const { data } = result;
    return new ResponsePayloadV2({
      pagination: new PaginationV2({
        page: data?.meta?.page ?? 1,
        limit: data?.meta?.limit ?? (Array.isArray(data?.data) ? data.data.length : 0),
        totalData: data?.meta?.total_records ?? (Array.isArray(data?.data) ? data.data.length : 0)
      }),
      data: Array.isArray(data?.data) ? data.data : []
    });
  }

  public convertCityListFromApi(result: AxiosResponse<any>): ResponsePayloadV2 {
    const { data } = result;
    return new ResponsePayloadV2({
      pagination: new PaginationV2({
        page: data?.meta?.page ?? 1,
        limit: data?.meta?.limit ?? (Array.isArray(data?.data) ? data.data.length : 0),
        totalData: data?.meta?.total_records ?? (Array.isArray(data?.data) ? data.data.length : 0)
      }),
      data: Array.isArray(data?.data) ? data.data : []
    });
  }

  public convertCommodityListFromApi(result: AxiosResponse<any>): ResponsePayloadV2 {
    const { data } = result;
    return new ResponsePayloadV2({
      pagination: new PaginationV2({
        page: data?.meta?.page ?? 1,
        limit: data?.meta?.limit ?? (Array.isArray(data?.data) ? data.data.length : 0),
        totalData: data?.meta?.total_records ?? (Array.isArray(data?.data) ? data.data.length : 0)
      }),
      data: Array.isArray(data?.data) ? data.data : []
    });
  }

  public convertRevenueBreakdownFromApi(result: AxiosResponse<any>): RevenueBreakdownData {
  const { data: { data } } = result;

  return new RevenueBreakdownData({
      comparisonChart: (data.comparison_chart || []).map((item: any) => new ChartPoint({
        date: item.date,
        totalRevenue: item.total_revenue,
        totalProfit: item.total_profit,
        totalStt: item.total_stt,
        totalWeightKg: item.total_weight_kg
      })),
      chart: (data.chart || []).map((item: any) => new ChartPoint({
        date: item.date,
        totalRevenue: item.total_revenue,
        totalProfit: item.total_profit,
        totalStt: item.total_stt,
        totalWeightKg: item.total_weight_kg,
        revenuePercentageChange: item.revenue_percentage_change,
        profitPercentageChange: item.profit_percentage_change,
        sttPercentageChange: item.stt_percentage_change,
        percentageChange: item.percentage_change
      })),
      breakdown: new RevenueDashboardBreakdownChartDataItem({
        codTypes: (data.breakdown.cod_types || []).map((item: any) => new RevenueDashboardBreakdownDataPerSection({
          name: item.name,
          total: item.total,
          percentageChange: item.percentage_change,
          data: item.data
        })),
        channels: (data.breakdown.channels || []).map((item: any) => new RevenueDashboardBreakdownDataPerSection({
          name: item.name,
          total: item.total,
          percentageChange: item.percentage_change,
          data: item.data
        })),
        productTypes: (data.breakdown.product_types || []).map((item: any) => new RevenueDashboardBreakdownDataPerSection({
          name: item.name,
          total: item.total,
          percentageChange: item.percentage_change,
          data: item.data
        })),
        destinationCities: (data.breakdown.destination_cities || []).map((item: any) => new RevenueDashboardBreakdownDataPerSection({
          name: item.name,
          total: item.total,
          percentageChange: item.percentage_change,
          data: item.data
        })),
        commodities: (data.breakdown.commodities || []).map((item: any) => new RevenueDashboardBreakdownDataPerSection({
          name: item.name,
          total: item.total,
          percentageChange: item.percentage_change,
          data: item.data
        }))
      }),
      lastSyncAt: data.last_sync_at
  });
  }
}
